/**
 * Shared builders for pages in the disability benefits app that use the V3 file input
 * (`va-file-input-multiple` via `fileInputMultipleUI`).
 *
 * The V3 upload pages share the same upload URL, payload shape, accepted
 * file types, hint text, and per-type size limits. Areas where pages differ:
 * - the form-data property key
 * - whether the upload is required (adds the `minItems: 1` schema backstop)
 * - whether a per-file document-type select is collected (`attachmentTypes`)
 *
 * These helpers are used for gated changes to inform all-claims-v2.
 * Potential follow-up: consolidate the upload pages for separation health
 * assessment and private medical records to use these helpers.
 */
import {
  fileInputMultipleUI,
  fileInputMultipleSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { MISSING_FILE } from 'platform/forms-system/src/js/validation';
import {
  FILE_TYPES,
  HINT_TEXT,
  UPLOAD_URL,
  FILE_UPLOAD_TITLE,
} from '../../../shared/components/fileInputComponent/constants';
import { additionalInput } from '../../components/fileInputComponent/PMRAdditionalUploadInput';
import {
  createPayload,
  parseResponse,
  handleAdditionalInput,
  additionalInputUpdate,
} from '../../../shared/utils/fileInputMultiUIConfig';
import {
  MAX_FILE_SIZE_BYTES,
  MAX_PDF_FILE_SIZE_BYTES,
} from '../../../shared/constants';

/** Shared per-type size limits: PDF gets the larger cap and a 1KB floor. */
export const V3_FILE_SIZES_BY_TYPE = {
  pdf: {
    maxFileSize: MAX_PDF_FILE_SIZE_BYTES,
    minFileSize: 1024,
  },
  default: {
    maxFileSize: MAX_FILE_SIZE_BYTES,
    minFileSize: 1,
  },
};

/** Shared confirmation field: list uploaded file names on the review page. */
export const v3UploadConfirmationField = ({ formData }) => ({
  data: formData?.map(item => item.name || item.fileName),
  label: 'Uploaded file(s)',
});

/**
 * Builds the uiSchema fragment for a V3 upload field.
 *
 * @param {object} options
 * @param {boolean} [options.required=true] - Whether an upload is required
 * @param {Array<{value: string, label: string}>} [options.attachmentTypes] -
 *   Options for a required per-file document-type select
 * @param {Function} [options.parseResponse] - Override for the response
 *   parser (for example, to add analytics tracking)
 * @param {string} [options.accept] - Override for the accepted file type
 *   list (defaults to the shared FILE_TYPES)
 * @param {string} [options.hint] - Override for the hint text (defaults to
 *   the shared HINT_TEXT)
 * @param {object} [options.fileSizesByFileType] - Override for the per-type
 *   size limits (defaults to V3_FILE_SIZES_BY_TYPE)
 * @returns {object} uiSchema fragment (spread alongside ui:confirmationField)
 */
export const v3UploadFieldUI = ({
  required = true,
  attachmentTypes,
  parseResponse: parseResponseOverride,
  accept = FILE_TYPES,
  hint = HINT_TEXT,
  fileSizesByFileType = V3_FILE_SIZES_BY_TYPE,
} = {}) => {
  return {
    ...fileInputMultipleUI({
      title: FILE_UPLOAD_TITLE,
      required,
      skipUpload: false,
      fileUploadUrl: UPLOAD_URL,
      formNumber: '21-526EZ',
      fileSizesByFileType,
      accept,
      hint,
      errorMessages: {
        ...(attachmentTypes && { additionalInput: 'Choose a document type' }),
        // Schema-level `minItems: 1` backstop (see v3UploadSchema): map the
        // empty-array error back to the standard missing-file message so
        // full-form submit matches interactive navigation.
        ...(required && { minItems: MISSING_FILE }),
      },
      createPayload,
      parseResponse: parseResponseOverride || parseResponse,
      ...(attachmentTypes && {
        additionalInputRequired: true,
        additionalInput: () => additionalInput({ attachmentTypes }),
        handleAdditionalInput,
        additionalInputUpdate,
      }),
    }),
    'ui:confirmationField': v3UploadConfirmationField,
  };
};

/**
 * Builds the schema for a V3 upload field.
 *
 * For required uploads, adds the `minItems: 1` backstop:
 * fileInputMultipleSchema() defaults to an empty array, so `required` alone
 * can't catch a skipped upload at full-form submit time — the custom
 * fileInputMultipleUI validator only runs on interactive page navigation.
 *
 * @param {object} options
 * @param {boolean} [options.required=true] - Whether an upload is required
 * @param {number} [options.maxItems] - Optional cap on the number of files
 * @returns {object} JSON schema for the upload array property
 */
export const v3UploadFieldSchema = ({ required = true, maxItems } = {}) => ({
  ...fileInputMultipleSchema(),
  ...(required && { minItems: 1 }),
  ...(maxItems && { maxItems }),
});
